'use client';

import { useEffect, useRef, useState } from 'react';

function getBaseName(name: string) {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(0, dot) : name;
}

export default function BrowserAddImageBorder() {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [fileName, setFileName] = useState('image');
  const [borderWidth, setBorderWidth] = useState(24);
  const [borderColor, setBorderColor] = useState('#111827');
  const [padding, setPadding] = useState(0);
  const [paddingColor, setPaddingColor] = useState('#ffffff');
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    return () => {
      if (downloadUrl) {
        URL.revokeObjectURL(downloadUrl);
      }
    };
  }, [downloadUrl]);

  function handleFile(file: File | undefined) {
    if (!file) return;
    setError(null);
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      setImage(img);
      setFileName(getBaseName(file.name) || 'image');
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      setError('This image format is not supported in your browser.');
      URL.revokeObjectURL(url);
    };
    img.src = url;
  }

  useEffect(() => {
    if (!image || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const iw = image.naturalWidth;
    const ih = image.naturalHeight;
    const total = borderWidth + padding;
    canvas.width = iw + total * 2;
    canvas.height = ih + total * 2;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (borderWidth > 0) {
      ctx.fillStyle = borderColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    if (padding > 0) {
      ctx.fillStyle = paddingColor;
      ctx.fillRect(
        borderWidth,
        borderWidth,
        canvas.width - borderWidth * 2,
        canvas.height - borderWidth * 2,
      );
    }
    ctx.drawImage(image, total, total, iw, ih);
    canvas.toBlob((blob) => {
      if (blob) {
        setDownloadUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(blob);
        });
      }
    }, 'image/png');
  }, [image, borderWidth, borderColor, padding, paddingColor]);

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Border</span>
        <h2 className="tool-live-title">Add a border to an image</h2>
        <p className="tool-live-subtitle">
          Upload an image and wrap it in a solid frame with an adjustable width, color, and optional
          inset padding. Everything runs in your browser and nothing is uploaded to a server.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <label className="tool-live-field tool-live-field-full">
            <span>Choose an image</span>
            <input
              accept="image/*"
              onChange={(event) => handleFile(event.target.files?.[0])}
              type="file"
            />
          </label>

          {image ? (
            <div className="tool-live-controls">
              <label className="tool-live-field tool-live-field-full">
                <span>Border width: {borderWidth} px</span>
                <input
                  max={200}
                  min={0}
                  onChange={(event) => setBorderWidth(Number(event.target.value))}
                  step={1}
                  type="range"
                  value={borderWidth}
                />
              </label>
              <label className="tool-live-field">
                <span>Border color</span>
                <input
                  onChange={(event) => setBorderColor(event.target.value)}
                  type="color"
                  value={borderColor}
                />
              </label>
              <label className="tool-live-field tool-live-field-full">
                <span>Inset padding: {padding} px</span>
                <input
                  max={200}
                  min={0}
                  onChange={(event) => setPadding(Number(event.target.value))}
                  step={1}
                  type="range"
                  value={padding}
                />
              </label>
              <label className="tool-live-field">
                <span>Padding color</span>
                <input
                  onChange={(event) => setPaddingColor(event.target.value)}
                  type="color"
                  value={paddingColor}
                />
              </label>
            </div>
          ) : null}

          <div className="tool-live-actions">
            {downloadUrl ? (
              <a
                className="button button-secondary"
                download={`${fileName}-border.png`}
                href={downloadUrl}
              >
                Download PNG
              </a>
            ) : null}
          </div>

          {error ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              {error}
            </p>
          ) : null}
          {image ? null : <p className="tool-live-panel-note">Pick an image to add a border.</p>}
        </section>

        <section className="tool-live-preview">
          <canvas
            ref={canvasRef}
            style={{
              maxWidth: '100%',
              height: 'auto',
              borderRadius: 12,
              display: image ? 'block' : 'none',
            }}
          />
          <p className="tool-live-panel-note">
            Processing runs locally with an HTML canvas. Your image never leaves your device.
          </p>
        </section>
      </div>
    </div>
  );
}
